import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DocusignModule,
  DocusignAddSignatureResult,
  DocusignDownloadResponse,
  DocusignDownloadResult,
} from '../models/docusign.model';

@Injectable({
  providedIn: 'root',
})
export class DocusignService {
  private readonly apiUrl = `${environment.apiUrl}/Docusign`;

  constructor(private http: HttpClient) {}

  /**
   * Raw JSON from `GET /api/Docusign/download/{documentId}` (includes `success: false` bodies).
   */
  getDownloadResponse(
    documentId: string
  ): Observable<DocusignDownloadResponse> {
    return this.http.get<DocusignDownloadResponse>(
      `${this.apiUrl}/download/${encodeURIComponent(documentId)}`
    );
  }

  /**
   * Build blob from a successful DocuSign download JSON body.
   * @throws Error if base64 payload is missing
   */
  parseDocusignFilePayload(res: DocusignDownloadResponse): DocusignDownloadResult {
    const b64 = res?.fileBase64 ?? res?.FileBase64;
    if (typeof b64 !== 'string' || !b64.trim()) {
      throw new Error(
        (res?.message && String(res.message)) || 'No document data in response'
      );
    }
    const fileName = (res.fileName ?? res.FileName ?? 'document.pdf').trim();
    const mime = this.mimeTypeFromFileName(fileName);
    const blob = this.base64StringToBlob(b64.replace(/\s/g, ''), mime);
    return { blob, fileName: fileName || 'document.pdf' };
  }

  /**
   * Download the signed/combined document (after workflow is complete).
   * GET /api/Docusign/download/{documentId}
   * Response: JSON with `fileBase64` and `fileName`.
   */
  downloadDocument(documentId: string): Observable<DocusignDownloadResult> {
    return this.getDownloadResponse(documentId).pipe(
      switchMap((res) => {
        if (res && res.success === false) {
          return throwError(
            () => new Error(res.message || 'Document download failed')
          );
        }
        try {
          return of(this.parseDocusignFilePayload(res));
        } catch (err) {
          return throwError(() =>
            err instanceof Error ? err : new Error(String(err))
          );
        }
      })
    );
  }

  private base64StringToBlob(base64: string, mimeType: string): Blob {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }

  private mimeTypeFromFileName(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    switch (ext) {
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'txt':
        return 'text/plain';
      default:
        return 'application/octet-stream';
    }
  }

  /**
   * Remove an abandoned envelope (e.g. user closed the DocuSign tab without finishing).
   * DELETE /api/Docusign/remove-envelope/{envelopeId}
   */
  removeEnvelope(envelopeId: string): Observable<string> {
    const url = `${this.apiUrl}/remove-envelope/${encodeURIComponent(
      envelopeId
    )}`;
    return this.http.delete<unknown>(url).pipe(
      map((body) => this.mapRemoveEnvelopeMessage(body))
    );
  }

  private mapRemoveEnvelopeMessage(body: unknown): string {
    if (body == null || body === '') {
      return 'Envelope record removed successfully';
    }
    if (typeof body === 'string') {
      const s = body.trim();
      if (!s) {
        return 'Envelope record removed successfully';
      }
      if (s.startsWith('{')) {
        try {
          return this.mapRemoveEnvelopeMessage(JSON.parse(s) as unknown);
        } catch {
          return s;
        }
      }
      return s;
    }
    if (typeof body === 'object' && body !== null) {
      const o = body as Record<string, unknown>;
      const msg = o['message'] ?? o['Message'];
      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim();
      }
    }
    return 'Envelope record removed successfully';
  }

  addDocusignSignature(
    documentId: string,
    module: DocusignModule
  ): Observable<DocusignAddSignatureResult> {
    return this.http
      .post<Record<string, unknown>>(`${this.apiUrl}/addsignature`, {
        documentId,
        module,
      })
      .pipe(
        map((body) => {
          const parsed = this.parseAddSignatureResponse(body);
          if (!parsed.signingUrl) {
            throw new Error('No signing URL in response');
          }
          return parsed;
        })
      );
  }

  private parseAddSignatureResponse(
    body: Record<string, unknown>
  ): DocusignAddSignatureResult {
    const nested = body['signingUrl'] ?? body['SigningUrl'];
    let signingUrl = '';
    let envelopeId = '';

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const n = nested as Record<string, unknown>;
      signingUrl = this.pickStringFromRecord(n, [
        'signingUrl',
        'SigningUrl',
        'url',
        'Url',
        'recipientViewUrl',
        'RecipientViewUrl',
      ]);
      envelopeId = this.pickStringFromRecord(n, [
        'envelopeId',
        'EnvelopeId',
      ]);
    }

    if (!signingUrl) {
      signingUrl = this.pickStringFromRecord(body, [
        'signingUrl',
        'SigningUrl',
        'url',
        'Url',
        'recipientViewUrl',
        'RecipientViewUrl',
      ]);
    }
    if (!envelopeId) {
      envelopeId = this.pickStringFromRecord(body, [
        'envelopeId',
        'EnvelopeId',
      ]);
    }

    return { signingUrl, envelopeId };
  }

  private pickStringFromRecord(
    obj: Record<string, unknown>,
    keys: string[]
  ): string {
    for (const k of keys) {
      const v = obj[k];
      if (typeof v === 'string' && v.trim()) {
        return v.trim();
      }
    }
    return '';
  }
}
